import React, {
  useMemo,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import type { RefObject } from "react";

import { DayTimePickerModal } from "@pianofunclub/react-native-paper-dates";
import { format } from "date-fns";
import { HStack, Text, Pressable, Box, Checkbox, useToast } from "native-base";
import { Platform } from "react-native";

import type { CellProps } from "pianofunclub-shared/components/Base/Cell";
import Row from "pianofunclub-shared/components/Base/Row";
import type { TableData } from "pianofunclub-shared/components/Base/Row";
import ManageRegisterLessonModal from "pianofunclub-shared/components/Modals/ManageRegisterLessonModal";
import type {
  LessonInfo as RegisterLessonInfo,
  RegisterUpdate,
} from "pianofunclub-shared/components/Modals/ManageRegisterLessonModal";
import type { ShowUpdateBlockDetailsModalHandler } from "pianofunclub-shared/components/Modals/UpdateLessonBlockDetailsModal";
import ToastAlert from "pianofunclub-shared/components/NativeBaseExtended/ToastAlert";
import LessonBlockProfile from "pianofunclub-shared/components/Registers/LessonBlockProfile";
import LessonStatus from "pianofunclub-shared/components/Registers/LessonStatus";
import type {
  LessonBlockDataNode,
  RegisterDates,
} from "pianofunclub-shared/components/Registers/TeacherRegisters";
import { customColors } from "pianofunclub-shared/theme";
import type { AccountType } from "pianofunclub-shared/types";
import { NO_STAGE } from "pianofunclub-shared/utils/constants";
import {
  combineNotes,
  getLessonDayString,
  getLessonTimeAsDate,
  schoolYearConverter,
  titleCaseConverter,
} from "pianofunclub-shared/utils/converters";
import { getFullName } from "pianofunclub-shared/utils/extractors";
import { ensureDateTypeIfDefined } from "pianofunclub-shared/utils/helpers";

export interface LessonBlockTableRowRef {
  setIsChecked: (isChecked: boolean) => void;
}

interface Props {
  cellProps?: CellProps;
  checkboxChangeHandler: (blockId: string, isSelected: boolean) => void;
  currentStartingYear: number;
  cursor: string;
  data: LessonBlockDataNode;
  datesToRender?: RegisterDates;
  flexArray: number[];
  isRegisterTable?: boolean;
  onPressEnrolment: (enrolmentId: string | null | undefined) => void;
  onPressProfile: (profileId?: string | null, accountType?: AccountType | null) => void;
  onPressSchool?: (schoolId?: string | null) => void;
  registerUpdates?: RefObject<RegisterUpdate[]>;
  rowHeight: number;
  selectedStartingYear: number;
  showUpdateBlockDetailsModalHandler: ShowUpdateBlockDetailsModalHandler;
  tableBorderColor?: string;
  tableBorderWidth?: number;
  updateRegisterState?: (
    variables: {
      lessonId: string;
      registerNote?: string;
      status: string;
    },
    progressChange?: number
  ) => void;
  updateScheduledLessonBlockTime?: (
    lessonBlockIds: string[],
    dayIndex: number,
    hours?: number,
    minutes?: number,
    onComplete?: () => void
  ) => void;
  userIsTeacher?: boolean;
}

const LessonBlockTableRow = forwardRef<LessonBlockTableRowRef, Props>((props, ref) => {
  const {
    cellProps: generalCellProps,
    checkboxChangeHandler,
    currentStartingYear,
    cursor,
    data,
    datesToRender,
    flexArray,
    isRegisterTable,
    onPressEnrolment,
    onPressProfile,
    onPressSchool,
    // used to modify pupil's school year based on the selected year
    registerUpdates,
    rowHeight,
    selectedStartingYear,
    showUpdateBlockDetailsModalHandler,
    tableBorderColor,
    tableBorderWidth,
    updateRegisterState,
    updateScheduledLessonBlockTime,
    userIsTeacher,
  } = props;

  const toast = useToast();

  const cellProps = useMemo(() => {
    if (generalCellProps) {
      return {
        ...generalCellProps,
        textProps: {
          ...generalCellProps.textProps,
          color: !data.pupil ? "surface.700" : undefined,
        },
      };
    }
  }, [data.pupil, generalCellProps]);

  const [checkBoxIsChecked, setCheckBoxIsChecked] = useState(false);
  const [dayTimePickerIsOpen, setDayTimePickerIsOpen] = useState(false);

  const intialiseScheduledDayTimeState = useCallback(() => {
    let hours, minutes: string | undefined;
    if (data.lessonTime) {
      [hours, minutes] = data.lessonTime.split(":").slice(0, 2);
    }
    return {
      dayIndex: data.lessonDay ?? undefined,
      hours: hours ? parseInt(hours) : undefined,
      minutes: minutes ? parseInt(minutes) : undefined,
    };
  }, [data.lessonDay, data.lessonTime]);

  const [scheduledDayTime, setScheduledDayTime] = useState<{
    dayIndex?: number;
    hours?: number;
    minutes?: number;
  }>(() => intialiseScheduledDayTimeState());

  useEffect(() => {
    setScheduledDayTime(() => intialiseScheduledDayTimeState());
  }, [intialiseScheduledDayTimeState]);

  const initialiseRegisterState = useCallback(
    (lessons: typeof data.lessons.edges) => {
      return (
        lessons.map((lesson) => {
          const existingUpdate = registerUpdates?.current.find(
            (item) => item.lessonId === lesson?.node?.id
          );
          return {
            staffRegisterNoteForTeacher:
              !userIsTeacher && existingUpdate?.registerNote
                ? existingUpdate.registerNote
                : lesson?.node?.staffRegisterNoteForTeacher
                  ? lesson?.node?.staffRegisterNoteForTeacher
                  : lesson?.node?.blacklistedDate?.reason,
            status: existingUpdate?.status ?? lesson?.node?.status ?? "NO_LESSON",
            teacherRegisterNoteForStaff:
              userIsTeacher && existingUpdate?.registerNote
                ? existingUpdate.registerNote
                : lesson?.node?.teacherRegisterNoteForStaff,
          };
        }) ?? []
      );
    },
    [data, registerUpdates, userIsTeacher]
  );

  const [lessonStatuses, setLessonStatuses] = useState(() =>
    initialiseRegisterState(data.lessons.edges ?? [])
  );

  useEffect(() => {
    setLessonStatuses(() => initialiseRegisterState(data.lessons.edges ?? []));
  }, [data.lessons.edges, initialiseRegisterState, intialiseScheduledDayTimeState]);

  const lessons = useMemo(() => {
    return data.lessons.edges ?? [];
  }, [data.lessons.edges]);

  const [registerModalLessonInfo, setRegisterModalLessonInfo] = useState<
    RegisterLessonInfo | undefined
  >(undefined);

  const onConfirmDayTime = useCallback(
    (params: { dayIndex: number; hours: number; minutes: number }) => {
      setDayTimePickerIsOpen(false);
      setScheduledDayTime({
        dayIndex: params.dayIndex - 1,
        hours: params.hours,
        minutes: params.minutes,
      });
      updateScheduledLessonBlockTime?.(
        [data.id],
        params.dayIndex - 1,
        params.hours,
        params.minutes
      );
    },
    [data.id, updateScheduledLessonBlockTime]
  );

  useImperativeHandle(ref, () => ({
    setIsChecked: (isChecked: boolean) => {
      setCheckBoxIsChecked(isChecked);
    },
  }));

  const rowData = useMemo(() => {
    const lessonDayString = getLessonDayString(data.lessonDay, {
      sliceToLength: 3,
    });
    const lessonTime = getLessonTimeAsDate(data.lessonTime);

    // this is to guard against an error when quickly changing days on the register
    let updatedLessonStatuses = lessonStatuses;
    if (lessonStatuses.length !== (data.lessons.edges ?? []).length) {
      updatedLessonStatuses = initialiseRegisterState(data.lessons.edges ?? []);
      setLessonStatuses(updatedLessonStatuses);
    }

    const activeEnrolmentOffer = data?.enrolmentOffers?.edges?.find(
      (offer) => offer?.node?.archived !== true && offer?.node?.offerAccepted !== true
    )?.node;

    const rowData: TableData = [
      {
        ...(lessonDayString && lessonTime
          ? {
              data: (
                <Box flex={1} mx="-2" px="1" width="100%">
                  <Text
                    {...cellProps?.textProps}
                    {...(!userIsTeacher
                      ? {
                          ...cellProps?.pressableTextProps,
                        }
                      : {})}
                    isTruncated
                    textAlign="center"
                  >{`${!isRegisterTable ? lessonDayString + " " : ""}${format(lessonTime, "H:mm")}
                                  ${
                                    data.lessonStage?.lessonDuration
                                      ? "-" +
                                        format(
                                          new Date(
                                            lessonTime.getTime() +
                                              data.lessonStage.lessonDuration * 60000
                                          ),
                                          "H:mm"
                                        )
                                      : ""
                                  }`}</Text>
                  {data?.staffNoteForTeacher || data?.teacherNoteForStaff ? (
                    <Box
                      bg="transparent"
                      borderRightColor="transparent"
                      borderRightWidth="10"
                      borderStyle="solid"
                      borderTopColor="primary.500"
                      borderTopWidth="10"
                      height="0"
                      position="absolute"
                      right="0"
                      style={{
                        transform: [{ rotate: "90deg" }],
                      }}
                      top={Platform.OS === "web" ? "-8" : "-4"}
                      width="0"
                    />
                  ) : null}
                </Box>
              ),
            }
          : { data: "Select Time", isPlaceholder: true }),
        onPress: !userIsTeacher ? () => setDayTimePickerIsOpen(true) : undefined,
        tooltipLabel: combineNotes(data?.teacherNoteForStaff, data?.staffNoteForTeacher),
      },
      // no need to show teacher if on a teacher's register page
      !isRegisterTable
        ? {
            ...(data.teacher
              ? {
                  data: (
                    <HStack flex={1} mr="-2" px="1" space="1" width="100%">
                      <Text
                        {...cellProps?.textProps}
                        {...(!userIsTeacher
                          ? {
                              ...cellProps?.pressableTextProps,
                            }
                          : {})}
                        flex={1}
                        isTruncated
                      >
                        {Platform.OS == "web"
                          ? getFullName(data.teacher.user.firstName, data.teacher.user.lastName)
                          : (data.teacher.user.firstName ?? "")}
                      </Text>
                      {!userIsTeacher && data.teacher ? (
                        <Pressable
                          alignItems="center"
                          justifyContent="center"
                          onPress={() =>
                            showUpdateBlockDetailsModalHandler({
                              lessonBlockIds: [data.id],
                              selectedProfileInfo: {
                                fullName: data.teacher?.user.firstName
                                  ? getFullName(
                                      data.teacher.user.firstName,
                                      data.teacher.user.lastName
                                    )
                                  : undefined,
                                id: data.teacher?.id,
                              },
                              type: "TEACHER",
                            })
                          }
                          px="1"
                        >
                          <Box
                            bg="transparent"
                            borderBottomColor="surface.500"
                            borderBottomWidth="9"
                            borderLeftColor="transparent"
                            borderLeftWidth="6"
                            borderRightColor="transparent"
                            borderRightWidth="6"
                            borderStyle="solid"
                            style={{
                              transform: [
                                {
                                  rotate: "180deg",
                                },
                              ],
                            }}
                          />
                        </Pressable>
                      ) : null}
                    </HStack>
                  ),
                }
              : {
                  data: "Select Teacher",
                  isPlaceholder: true,
                }),
            onPress: () => {
              if (data.teacher) {
                onPressProfile(data.teacher.id, "TEACHER");
              } else {
                showUpdateBlockDetailsModalHandler({
                  lessonBlockIds: [data.id],
                  type: "TEACHER",
                });
              }
            },
          }
        : {
            ...(data.school?.name
              ? {
                  data: data.school.name,
                }
              : {
                  data: !userIsTeacher ? "Select School" : "",
                  isPlaceholder: true,
                }),
            onPress: !userIsTeacher
              ? () => {
                  if (data.school) {
                    onPressSchool?.(data.school.id);
                  }
                }
              : undefined,
          },
      ...(Platform.OS === "web"
        ? [
            {
              ...(data.type === "BREAK"
                ? { data: "" }
                : data.lessonStage
                  ? {
                      data:
                        data.lessonStage.lessonType === "INDIVIDUAL"
                          ? "1-on-1"
                          : data.lessonStage.lessonType === "PAIRED"
                            ? "Paired"
                            : "Group",
                    }
                  : {
                      data: !userIsTeacher ? "Select Type" : "",
                      isPlaceholder: true,
                    }),
              onPress:
                !userIsTeacher && data.type !== "BREAK"
                  ? () => {
                      showUpdateBlockDetailsModalHandler({
                        lessonBlockCursor: cursor,
                        lessonBlockIds: [data.id],
                        schoolId: data.school?.id,
                        type: "STAGE",
                      });
                    }
                  : undefined,
            },
            {
              ...(data.type === "BREAK"
                ? { data: "" }
                : data.lessonStage
                  ? {
                      data:
                        data.lessonStage.stage !== NO_STAGE
                          ? (data.lessonStage.stage?.toString() ?? "")
                          : "Any Stage",
                    }
                  : {
                      data: !userIsTeacher ? "Select Stage" : "",
                      isPlaceholder: true,
                    }),
              onPress:
                !userIsTeacher && data.type !== "BREAK"
                  ? () => {
                      showUpdateBlockDetailsModalHandler({
                        lessonBlockCursor: cursor,
                        lessonBlockIds: [data.id],
                        schoolId: data.school?.id,
                        type: "STAGE",
                      });
                    }
                  : undefined,
            },
          ]
        : []),
      {
        ...(data.type !== "BREAK"
          ? data.instrument
            ? {
                data: titleCaseConverter(data.instrument.name) ?? "",
              }
            : {
                data: "Select Instrument",
                isPlaceholder: true,
              }
          : {
              data: "",
            }),
        onPress: () => {
          showUpdateBlockDetailsModalHandler({
            lessonBlockIds: [data.id],
            type: "INSTRUMENT",
          });
        },
      },
      {
        ...(data.type !== "BREAK"
          ? data.pupil
            ? {
                data: (
                  <LessonBlockProfile
                    hasNote={Boolean(
                      data.pupil?.teacherNoteForStaff || data.pupil?.staffNoteForTeacher
                    )}
                    onPressDropdown={() => {
                      if (!data.instrument?.name || !data.lessonStage || !data.teacher) {
                        toast.show({
                          render: ({ id }: { id: string }) => (
                            <ToastAlert
                              id={id}
                              status="info"
                              title={
                                "Please choose an instrument, lesson stage and teacher before selecting a pupil"
                              }
                              toast={toast}
                            />
                          ),
                        });
                        return;
                      }

                      showUpdateBlockDetailsModalHandler({
                        keepFree: data.keepFree,
                        lessonBlockDataForPupilChange: data,
                        lessonBlockIds: [data.id],
                        schoolId: data.school?.id,
                        selectedProfileInfo: {
                          fullName: data.pupil?.user.firstName
                            ? getFullName(data.pupil.user.firstName, data.pupil.user.lastName)
                            : undefined,
                          id: data.pupil?.id,
                        },
                        type: "PUPIL",
                      });
                    }}
                    pressableTextProps={cellProps?.pressableTextProps}
                    profileFirstName={data.pupil.user.firstName}
                    profileLastName={data.pupil.user.lastName}
                    textProps={cellProps?.textProps}
                    userIsTeacher={userIsTeacher}
                  />
                ),
              }
            : activeEnrolmentOffer
              ? {
                  data: (
                    <LessonBlockProfile
                      onPressDropdown={() => {
                        if (!data.instrument?.name || !data.lessonStage || !data.teacher) {
                          toast.show({
                            render: ({ id }: { id: string }) => (
                              <ToastAlert
                                id={id}
                                status="info"
                                title={
                                  "Please choose an instrument, lesson stage and teacher before selecting a pupil"
                                }
                                toast={toast}
                              />
                            ),
                          });
                          return;
                        }

                        showUpdateBlockDetailsModalHandler({
                          keepFree: data.keepFree,
                          lessonBlockDataForPupilChange: data,
                          lessonBlockIds: [data.id],
                          schoolId: data.school?.id,
                          selectedProfileInfo: {
                            enrolmentStatus: activeEnrolmentOffer.enrolment.status,
                            firstName: activeEnrolmentOffer.enrolment.pupilFirstName,
                            fullName: getFullName(
                              activeEnrolmentOffer.enrolment.pupilFirstName,
                              activeEnrolmentOffer.enrolment.pupilLastName
                            ),
                            id: activeEnrolmentOffer.enrolment.id,
                            parentName: activeEnrolmentOffer.enrolment.parentName,
                          },
                          type: "PUPIL",
                        });
                      }}
                      pressableTextProps={cellProps?.pressableTextProps}
                      profileFirstName={activeEnrolmentOffer.enrolment.pupilFirstName}
                      profileLastName={activeEnrolmentOffer.enrolment.pupilLastName}
                      textProps={{
                        ...cellProps?.textProps,
                        ...cellProps?.placeholderTextProps,
                      }}
                      userIsTeacher={userIsTeacher}
                    />
                  ),
                }
              : {
                  data: data?.keepFree
                    ? "Keep Free"
                    : !userIsTeacher
                      ? "Select Pupil"
                      : "Free Slot",
                  isPlaceholder: !data?.keepFree,
                }
          : {
              data: "",
            }),
        onPress: !userIsTeacher
          ? () => {
              if (data.pupil) {
                onPressProfile(data.pupil.id, "PUPIL");
              } else if (activeEnrolmentOffer) {
                onPressEnrolment(activeEnrolmentOffer?.enrolment?.id);
              } else {
                if (!data.instrument?.name || !data.lessonStage || !data.teacher) {
                  toast.show({
                    render: ({ id }: { id: string }) => (
                      <ToastAlert
                        id={id}
                        status="info"
                        title={
                          "Please choose an instrument, lesson stage and teacher before selecting a pupil"
                        }
                        toast={toast}
                      />
                    ),
                  });
                  return;
                }

                showUpdateBlockDetailsModalHandler({
                  keepFree: data.keepFree,
                  lessonBlockDataForPupilChange: data,
                  lessonBlockIds: [data.id],
                  schoolId: data.school?.id,
                  type: "PUPIL",
                });
              }
            }
          : undefined,
        tooltipLabel:
          data.type !== "BREAK"
            ? data.pupil
              ? combineNotes(data?.pupil?.teacherNoteForStaff, data?.pupil?.staffNoteForTeacher)
              : undefined
            : undefined,
      },
      {
        data:
          data.type !== "BREAK"
            ? schoolYearConverter(data.pupil?.schoolYear, {
                currentStartingYear,
                selectedStartingYear,
                short: true,
              })
            : "",
      },
    ];

    if (isRegisterTable) {
      if (data.pupil) {
        rowData.push(
          ...(datesToRender?.map((dateToRender, index) => {
            // first attempt to match lesson to the scheduled header date
            let lesson = lessons.find(
              (item) =>
                ensureDateTypeIfDefined(item?.node?.scheduledTimestamp)?.toDateString() ===
                dateToRender?.scheduledDate?.toDateString()
            );

            if (!lesson) {
              // if there is no match, we attempt to match with the actual header date
              // this covers cases where a lesson week was rearranged for another school in the register
              // to the same date as the current school's lessons
              lesson = lessons.find(
                (item) =>
                  ensureDateTypeIfDefined(item?.node?.scheduledTimestamp)?.toDateString() ===
                  dateToRender?.actualDate?.toDateString()
              );
            }

            if (!lesson?.node) {
              return {
                data: "",
              };
            }

            // check for cases where the lesson date does not match the header date
            // this might be because a lesson was rearranged, or because the lessons at another school
            // in the same register were rearranged to a different date
            const dateDoesNotMatchHeader =
              dateToRender?.actualDate?.toDateString() !==
              ensureDateTypeIfDefined(
                lesson?.node?.rearrangedTimestamp ?? lesson?.node?.scheduledTimestamp
              )?.toDateString();

            const wasRearrangedToDifferentDate =
              dateDoesNotMatchHeader &&
              Boolean(lesson?.node?.rearrangedTimestamp) &&
              ensureDateTypeIfDefined(lesson?.node?.rearrangedTimestamp)?.toDateString() !==
                ensureDateTypeIfDefined(lesson?.node?.scheduledTimestamp)?.toDateString();

            return {
              data: (
                <LessonStatus
                  hasRegisterNote={Boolean(
                    updatedLessonStatuses[index].teacherRegisterNoteForStaff ||
                      updatedLessonStatuses[index].staffRegisterNoteForTeacher
                  )}
                  height="39px"
                  hideLabel
                  isRearranged={dateDoesNotMatchHeader}
                  mx="-2"
                  status={updatedLessonStatuses[index].status}
                />
              ),
              onPress:
                // if register is break, do not make the lesson marking editable
                data.type !== "BREAK"
                  ? () => {
                      if (lesson?.node) {
                        // if register is blank, default to marking as present
                        if (updatedLessonStatuses[index].status === "BLANK") {
                          const clonedLessonStatuses = updatedLessonStatuses.slice();
                          clonedLessonStatuses[index].status = "PRESENT";
                          setLessonStatuses(clonedLessonStatuses);
                          updateRegisterState?.(
                            {
                              lessonId: lesson.node.id,
                              status: "PRESENT",
                            },
                            1
                          );
                        } else {
                          setRegisterModalLessonInfo({
                            id: lesson.node.id,
                            index: index,
                            instrument: titleCaseConverter(data.instrument?.name),
                            pupilName: getFullName(
                              data.pupil?.user.firstName,
                              data.pupil?.user.lastName
                            ),
                            scheduledTimestamp: lesson.node.scheduledTimestamp
                              ? format(
                                  new Date(
                                    lesson?.node?.rearrangedTimestamp ??
                                      lesson.node.scheduledTimestamp
                                  ),
                                  "EEE d MMM HH:mm"
                                )
                              : undefined,
                            staffRegisterNoteForTeacher:
                              updatedLessonStatuses[index].staffRegisterNoteForTeacher ?? undefined,
                            status: updatedLessonStatuses[index].status ?? "BLANK",
                            teacherRegisterNoteForStaff:
                              updatedLessonStatuses[index].teacherRegisterNoteForStaff ?? undefined,
                          });
                        }
                      }
                    }
                  : undefined,
              tooltipLabel: combineNotes(
                updatedLessonStatuses[index].teacherRegisterNoteForStaff,
                updatedLessonStatuses[index].staffRegisterNoteForTeacher,
                dateDoesNotMatchHeader
                  ? (lesson?.node?.rearrangedTimestamp ?? lesson?.node?.scheduledTimestamp)
                  : undefined,
                !wasRearrangedToDifferentDate
              ),
            };
          }) ?? [])
        );
      } else {
        rowData.push(
          ...(lessons?.map((lesson, index) => {
            return {
              data: (
                <LessonStatus
                  height="39px"
                  hideBlankIcon
                  hideDropdownIcon
                  hideLabel
                  mx="-2"
                  status={updatedLessonStatuses[index].status}
                />
              ),
              // If register is break, do not make the lesson marking editable
              onPress:
                data.type !== "BREAK"
                  ? () => {
                      if (lesson?.node) {
                        const clonedLessonStatuses = updatedLessonStatuses.slice();
                        const newStatus =
                          updatedLessonStatuses[index].status === "BLANK" ? "NO_LESSON" : "BLANK";
                        clonedLessonStatuses[index].status = newStatus;
                        setLessonStatuses(clonedLessonStatuses);
                        updateRegisterState?.({
                          lessonId: lesson.node.id,
                          status: newStatus,
                        });
                      }
                    }
                  : undefined,
            };
          }) ?? [])
        );
      }
    }

    if (!userIsTeacher) {
      rowData.unshift({
        data: (
          <Checkbox
            isChecked={checkBoxIsChecked}
            onChange={(isSelected) => {
              setCheckBoxIsChecked(isSelected);
              checkboxChangeHandler(data.id, isSelected);
            }}
            size="md"
            value={data.id}
          />
        ),
        onPress: () => {
          return;
        },
      });
    }
    return rowData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data.lessonDay,
    data.lessonTime,
    data.lessons.edges,
    data.lessonStage,
    data?.staffNoteForTeacher,
    data?.teacherNoteForStaff,
    data.teacher,
    data.school,
    data.type,
    data.instrument,
    data.pupil,
    data.keepFree,
    data.id,
    data?.enrolmentOffers?.edges,
    lessonStatuses,
    cellProps?.textProps,
    cellProps?.pressableTextProps,
    userIsTeacher,
    isRegisterTable,
    selectedStartingYear,
    currentStartingYear,
    initialiseRegisterState,
    showUpdateBlockDetailsModalHandler,
    onPressProfile,
    onPressSchool,
    cursor,
    datesToRender,
    lessons,
    updateRegisterState,
    checkBoxIsChecked,
    checkboxChangeHandler,
  ]);

  const updateRegisterHandler = useCallback(
    (
      variables: {
        lessonId: string;
        staffRegisterNoteForTeacher?: string | undefined;
        status: string;
        teacherRegisterNoteForStaff?: string | undefined;
      },
      lessonIndex: number,
      progressChange?: number | undefined
    ) => {
      const clonedLessonStatuses = lessonStatuses.slice();
      clonedLessonStatuses[lessonIndex].status = variables.status;
      if (variables.teacherRegisterNoteForStaff) {
        clonedLessonStatuses[lessonIndex].teacherRegisterNoteForStaff =
          variables.teacherRegisterNoteForStaff;
      }
      if (variables.staffRegisterNoteForTeacher) {
        clonedLessonStatuses[lessonIndex].staffRegisterNoteForTeacher =
          variables.staffRegisterNoteForTeacher;
      }
      clonedLessonStatuses[lessonIndex].status = variables.status;
      setLessonStatuses(clonedLessonStatuses);
      updateRegisterState?.(variables, progressChange);
    },
    [lessonStatuses, updateRegisterState]
  );

  return (
    <>
      <Row
        bg={
          data.type === "BREAK"
            ? "yellow.100"
            : !data.pupil
              ? "muted.200"
              : data.lessonStage?.lessonType === "INDIVIDUAL"
                ? "transparent"
                : data.lessonStage?.lessonType === "PAIRED"
                  ? "secondary.100"
                  : "primary.50"
        }
        cellProps={cellProps}
        data={rowData}
        flexArray={flexArray}
        rowHeight={rowHeight}
        rowIndex={0}
        tableBorderColor={tableBorderColor}
        tableBorderWidth={tableBorderWidth}
      />
      <DayTimePickerModal
        accentColor={customColors.primary[800]}
        animationType="fade"
        dayIndex={(scheduledDayTime.dayIndex ?? 0) + 1}
        duration={data.lessonStage?.lessonDuration}
        hours={scheduledDayTime.hours ?? 9}
        label="Reschedule Lesson Slot"
        locale="en"
        minutes={scheduledDayTime.minutes ?? 0}
        onConfirm={onConfirmDayTime}
        onDismiss={() => setDayTimePickerIsOpen(false)}
        saveLabel="Save"
        textStyle={{
          fontFamily: "Poppins-SemiBold",
        }}
        uppercase={false}
        visible={dayTimePickerIsOpen}
      />
      {updateRegisterState ? (
        <ManageRegisterLessonModal
          hideModal={() => setRegisterModalLessonInfo(undefined)}
          lessonInfo={registerModalLessonInfo}
          showModal={registerModalLessonInfo !== undefined}
          updateRegister={updateRegisterHandler}
          userIsTeacher={userIsTeacher}
        />
      ) : null}
    </>
  );
});

export default React.memo(LessonBlockTableRow);
